import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowBack as BackIcon, Checklist as CountIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import { useAuth } from '../../../../context/AuthContext';
import stockCountService from '../../../../services/stockCountService';
import venueLocationService, { pickDefaultLocation } from '../../../../services/venueLocationService';
import type { VenueLocation } from '../../../../types/venue';

// Today in the user's own calendar. toISOString() gives UTC's date, which in
// Hong Kong is yesterday until 8am.
const todayISO = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const StockCountForm: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId } = useVenue();
  const { user } = useAuth();
  const backPath = `/admin/venues/${venueId}/stock-counts`;

  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  // A stock count must be scoped to a specific location. '' = nothing selected yet.
  const [scopeValue, setScopeValue] = useState<string>('');
  const [countedAt, setCountedAt] = useState(todayISO());
  const [countedBy, setCountedBy] = useState('');
  const [notes, setNotes] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // INVENTORY-TRUST.1b — set when the backend rejects with DRAFT_EXISTS_FOR_LOCATION.
  const [existingDraftId, setExistingDraftId] = useState<string | null>(null);
  const [openingDraft, setOpeningDraft] = useState(false);

  /**
   * COUNT-DATE.3 — carry what was just typed into the draft being continued.
   *
   * A location allows one open draft, so "New Stock Count" on a location that
   * has one is refused and offers the existing count instead. Following that
   * offer used to drop the date and Counted By the user had just filled in. On
   * production an undated draft was continued twice this way, counted in full,
   * and then refused at Review for want of the date they had entered.
   *
   * Only empty fields are filled: a draft someone else dated keeps its date.
   * Navigation goes ahead even if the save fails — the draft is still the
   * right place to count, and Review still guards the post.
   */
  const openExistingDraft = async () => {
    if (!existingDraftId) return;
    setOpeningDraft(true);
    try {
      const { count: draft } = await stockCountService.getCount(venueId, existingDraftId);
      const fill: { countedAt?: string; countedBy?: string; notes?: string } = {};
      if (!draft.countedAt && countedAt) fill.countedAt = countedAt;
      if (!draft.countedBy?.trim() && countedBy.trim()) fill.countedBy = countedBy.trim();
      if (!draft.notes?.trim() && notes.trim()) fill.notes = notes.trim();
      if (Object.keys(fill).length > 0) {
        await stockCountService.updateCount(venueId, existingDraftId, fill);
      }
    } catch { /* still open it — see above */ }
    navigate(`/admin/venues/${venueId}/stock-counts/${existingDraftId}`);
  };

  useEffect(() => {
    if (user) {
      setCountedBy((user as any).email ?? (user as any).firstName ?? '');
    }
  }, [user]);

  useEffect(() => {
    if (!venueId) return;
    venueLocationService.getLocations(venueId)
      .then(locs => {
        const active = locs.filter(l => l.active);
        setLocations(active);
        const def = pickDefaultLocation(active);
        if (def) setScopeValue(def._id);
      })
      .catch(() => {})
      .finally(() => setLocationsLoading(false));
  }, [venueId]);

  const handleCreate = async () => {
    setError(null);
    setExistingDraftId(null);
    if (!scopeValue) {
      setError('Select a location for this stock count.');
      return;
    }
    if (!countedAt) {
      setError('Count date is required.');
      return;
    }
    setSaving(true);
    try {
      const count = await stockCountService.createCount(venueId, {
        locationId: scopeValue,
        countedAt,
        countedBy: countedBy.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      navigate(`/admin/venues/${venueId}/stock-counts/${count._id}`);
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      if (apiError?.code === 'DRAFT_EXISTS_FOR_LOCATION') {
        setExistingDraftId(apiError.existingCountId ?? null);
        setError('A draft stock count is already open for this location. Continue counting in it, or finish or delete it before starting a new one. Opening it adds the date and Counted By you entered, if it has none.');
      } else {
        setError(apiError?.message ?? err?.response?.data?.message ?? err?.message ?? 'Failed to create stock count');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', maxWidth: 640 }}>
        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', gap: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
          >
            Stock Counts
          </Button>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CountIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
            <Typography variant="h6" fontWeight={600}>New Stock Count</Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {error && (
            <Alert
              severity={existingDraftId ? 'warning' : 'error'}
              action={existingDraftId ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={openExistingDraft}
                  disabled={openingDraft}
                  sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  Open the existing count
                </Button>
              ) : undefined}
            >
              {error}
            </Alert>
          )}

          {/* Scope selector — a stock count must target one location. */}
          <Box>
            {!locationsLoading && locations.length === 0 ? (
              <Alert
                severity="warning"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => navigate(`/admin/venues/${venueId}/locations`)}
                    sx={{ textTransform: 'none' }}
                  >
                    Add location
                  </Button>
                }
              >
                This venue has no active locations. Create a location first — stock counts must be scoped to a specific location.
              </Alert>
            ) : (
              <TextField
                select
                fullWidth
                required
                label="Location"
                value={scopeValue}
                onChange={e => setScopeValue(e.target.value)}
                disabled={locationsLoading}
                error={!locationsLoading && !scopeValue}
                helperText="A count is scoped to one location. Items from other locations cannot be added."
                size="small"
              >
                {locations.map(loc => (
                  <MenuItem key={loc._id} value={loc._id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{loc.displayName ?? loc.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{loc.type}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          {/* Date + counted by */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Count Date"
              type="date"
              value={countedAt}
              onChange={e => setCountedAt(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="When the physical count happened"
              required
            />
            <TextField
              label="Counted By"
              value={countedBy}
              onChange={e => setCountedBy(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              placeholder="Name or initials"
            />
          </Box>

          {/* Notes */}
          <TextField
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="Optional"
          />

          {/* Actions */}
          <Box sx={{
            pt: 1.5,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            display: 'flex', gap: 1.5, justifyContent: 'flex-end',
          }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={saving || !countedAt || !scopeValue}
              sx={{ textTransform: 'none', fontWeight: 600 }}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {saving ? 'Creating…' : 'Create Stock Count'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default StockCountForm;
